import React from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockButton from '../components/GuideMockButton';
import GuideMockChip from '../components/GuideMockChip';
import GuideMockContainer from '../components/GuideMockContainer';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuideClientDetail: React.FC = () => {
  return (
    <GuidePageLayout
      title="取引先の詳細情報を確認する"
      description="取引先の登録内容を詳細に確認します。"
      breadcrumbs={[{ label: '取引先管理', path: '/guide/client/overview' }]}
    >
      <GuideSection title="前提">
        <GuideTable
          headers={['条件', '内容']}
          rows={[
            ['必要な権限', '管理者 / 管理部門 / 課長'],
            ['ログイン状態', 'ログイン済みであること'],
          ]}
        />
      </GuideSection>

      <GuideSection title="手順">
        <GuideStepList
          steps={[
            { label: '取引先管理画面を開く' },
            { label: '取引先一覧から対象取引先の行をクリックする' },
            {
              label: '「取引先詳細」が右パネルに表示される',
              description:
                '取引先の全情報が参照専用で表示されます。ヘッダー右上にIDとコピーボタンが表示されます。',
              content: (
                <GuideMockContainer label="取引先詳細（参照モード）">
                  <Stack spacing={1}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ pointerEvents: 'none' }}
                    >
                      <IconButton size="small">
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="h6" fontWeight={700}>
                        取引先詳細
                      </Typography>
                      <GuideMockChip label="SIer" color="info" />
                      <GuideMockChip label="取引中" color="primary" />
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: 'auto' }}>
                        <Typography variant="caption" color="text.secondary">
                          ID: xxxxxxxx-xxxx-xxxx-xxxx
                        </Typography>
                        <IconButton size="small">
                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Stack>
                </GuideMockContainer>
              ),
            },
          ]}
        />
      </GuideSection>

      <GuideSection title="表示される情報">
        <GuideTable
          headers={['項目', '説明']}
          rows={[
            ['取引先名 / カナ', '取引先企業名'],
            ['取引先区分', 'SIer / エンドユーザー / SES等'],
            ['取引開始日 / 取引終了日', '取引期間（yyyy/mm/dd形式）'],
            ['法人番号 / 登録番号', '法人情報'],
            ['住所 / 企業ホームページ', '企業情報'],
            ['担当者名 / 電話番号 / メールアドレス', '連絡先情報'],
            ['登録者 / 登録日', '操作者情報'],
            ['備考', '自由入力メモ'],
          ]}
        />
      </GuideSection>

      <GuideSection title="ボタンと操作">
        <GuideTable
          headers={['操作', '動作']}
          rows={[
            ['← 戻る矢印', '詳細画面を閉じて一覧に戻る'],
            ['IDコピーボタン', '取引先IDをクリップボードにコピー'],
            ['編集ボタン', '編集モードに切り替える（取引先情報を更新するガイドを参照）'],
          ]}
        />
        <Stack sx={{ mt: 1 }}>
          <GuideMockContainer label="フッターボタン">
            <GuideMockButton label="編集" startIcon={<EditIcon />} />
          </GuideMockContainer>
        </Stack>
      </GuideSection>

      <GuideSection title="注意事項">
        <GuideInfoBox>
          登録者が削除済みユーザーの場合、登録者名の代わりにユーザーIDが表示されます。
        </GuideInfoBox>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideClientDetail;
